<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t("operaciones_factura.titulo") }}</h1>
      <div class="toolbar">
      </div>
    </div>

    <div class="text-muted mb-2 cursive"> {{  $t("operaciones_factura.informacion_reajuste") }} </div>

    <DxDataGrid
      v-bind="tramitesGridOptions"
      :data-source="filasFactura"
      :key-expr="claveFilaFacturaKeyExpr"
      ref="grid"
      class="dx-card wide-card grid-liquidaciones"
      @selection-changed="onSelectionChanged"
    >
      <DxSelection
        :mode="sePermiteReajusteConceptos ? 'multiple' : 'none'"
        select-all-mode="page"
        :show-check-boxes-mode="sePermiteReajusteConceptos ? 'always' : 'none'"
      />
      <DxToolbar>
        <DxItem location="after" template="reajusteComisionBtn" :visible="sePermiteReajusteConceptos" />
        <DxItem location="after" template="reajusteRetroBtn" :visible="sePermiteReajusteConceptos" />
        <DxItem location="after" template="aplicarReajustesBtn" :visible="sePermiteReajusteConceptos" />
        <DxItem location="after" template="exportButtonTemplate" />
      </DxToolbar>
      <DxColumn :group-index="0" data-field="NombreTramite" group-cell-template="tramiteGroupTemplate" />
      <DxColumn data-field="CodigoSecuencial" :caption="$t('liquidaciones.grid.id')" format="0000000000"></DxColumn>
      <DxColumn data-field="TipoConcepto" :caption="$t('liquidaciones.grid.tipo_concepto')">
        <DxLookup column-auto-width="true" v-bind="getLookup('liquidaciones', 'tipo_concepto')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="FechaActivacion" :caption="$t('liquidaciones.grid.fecha_activacion')" data-type="date"
        format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="CodigoEstadoLiquidacion" :caption="$t('operaciones.grid.liquidado')" cell-template="liquidadoTemplate">
        <DxLookup v-bind="getLookup('operaciones', 'liquidado')" />
      </DxColumn>
      <DxColumn data-field="ComisionEfectiva" :caption="$t('operaciones.grid.comision')" cell-template="desgloseComisionTemplate" />
      <DxColumn data-field="RetrocomisionEfectiva" :caption="$t('operaciones.grid.retrocomision')" cell-template="desgloseRetrocomisionTemplate" />
      <DxColumn data-field="Comercializadora" :caption="$t('liquidaciones.grid.comercializadora')"></DxColumn>
      <DxColumn data-field="NombreAgente" :caption="$t('liquidaciones.grid.agente')"></DxColumn>
      <DxColumn data-field="DocumentoTitular" :caption="$t('liquidaciones.grid.documento_cliente')"></DxColumn>
      <DxColumn data-field="DireccionTitular" :caption="$t('liquidaciones.grid.direccion_cliente')"></DxColumn>
      <DxColumn data-field="IdTipoSuministro" :caption="$t('operaciones.grid.suministro')">
        <DxLookup column-auto-width="true" v-bind="getLookup('operaciones', 'tipo_suministro')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="Cups" :caption="$t('operaciones.grid.cups')"></DxColumn>

      <DxColumn type="buttons" :caption="$t('liquidaciones.grid.acciones')" :fixed="true" fixed-position="right" width="100px" :buttons="[
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('liquidaciones.grid.ver_detalle'),
          icon: 'fa-solid fa-magnifying-glass text-success',
          onClick: (e: any) => verDetalle(e)
        }
      ]" />

      <DxSummary>
        <DxGroupItem :show-in-group-footer="false" :align-by-column="true" column="ComisionEfectiva" summary-type="sum"
          display-format="Total: {0}" :value-format="formatTotalComision">
        </DxGroupItem>
        <DxGroupItem :show-in-group-footer="false" :align-by-column="true" column="RetrocomisionEfectiva" summary-type="sum"
          display-format="Total: {0}" :value-format="formatTotalRetrocomision">
        </DxGroupItem>
      </DxSummary>

      <DxColumnFixing :enabled="true" />

      <template #tramiteGroupTemplate="{ data }">
        <span>
          {{ data.value }}
        </span>
      </template>
      <template #liquidadoTemplate="{ data }">
        <div :class="[
          'd-inline-block rounded py-1 px-2',
          `estado-liquidacion ${data.value.replace(' ', '')}`,
        ]">
          {{ $t(`operaciones.liquidado.${data.value}`) }}
        </div>
      </template>
      <template #desgloseComisionTemplate="{ data }">
        <DesgloseRetribucionEconomica
          :importe-total="data.data.ComisionEfectiva"
          :importe-calculado="data.data.Comision"
          :importe-reajuste="data.data.ReajusteComision"
          :motivo-reajuste="data.data.MotivoReajusteComision"
        />
      </template>
      <template #desgloseRetrocomisionTemplate="{ data }">
        <DesgloseRetribucionEconomica
          :importe-total="data.data.RetrocomisionEfectiva"
          :importe-calculado="data.data.Retrocomision"
          :importe-reajuste="data.data.ReajusteRetrocomision"
          :motivo-reajuste="data.data.MotivoReajusteRetrocomision"
          es-retrocomision
        />
      </template>
      <template #reajusteComisionBtn>
        <button
          type="button"
          class="btn btn-sm btn-light d-flex align-items-center gap-2 m-2"
          :disabled="!puedeAsignarReajusteComision"
          @click="onAsignarReajusteComision"
        >
          <i class="fas fa-euro-sign text-primary"></i>
          <span class="text-primary">{{ $t("operaciones_factura.grid.reajuste_comision") }}</span>
        </button>
      </template>
      <template #reajusteRetroBtn>
        <button
          type="button"
          class="btn btn-sm btn-light d-flex align-items-center gap-2 m-2"
          :disabled="!puedeAsignarReajusteRetrocomision"
          @click="onAsignarReajusteRetrocomision"
        >
          <i class="fas fa-rotate-left text-primary"></i>
          <span class="text-primary">{{ $t("operaciones_factura.grid.reajuste_retrocomision") }}</span>
        </button>
      </template>
      <template #aplicarReajustesBtn>
        <button
          type="button"
          class="btn btn-sm btn-light d-flex align-items-center gap-2 m-2"
          :disabled="!puedeAplicarReajustes"
          @click="onAplicarReajustes"
        >
          <i class="fas fa-check text-success"></i>
          <span class="text-success">{{ $t("operaciones_factura.grid.aplicar_reajustes") }}</span>
        </button>
      </template>
      <template #exportButtonTemplate>
        <button type="button" @click="onExporting" class="btn btn-sm btn-light d-flex align-items-center gap-2 m-2">
          <i class="fas fa-file-excel exportar-excel"></i>
          <span class="text-primary">{{ $t("shared.ui.buttons.exportToExcel") }}</span>
        </button>
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { useApi } from "@api/integration";
import {
  DxDataGrid,
  DxColumnFixing,
  DxColumn,
  DxLookup,
  DxSummary,
  DxGroupItem,
  DxItem,
  DxToolbar,
  DxSelection,
  type DxDataGridTypes,
} from "devextreme-vue/data-grid";
import {
  getDefaultGridProperties,
  getLookup,
  exportGridToExcel,
} from "@helpers/grid-helper";
import { computed, nextTick, ref, shallowRef, triggerRef, watch } from "vue";
import type DevExpress from "devextreme";
import type {
  AplicarReajustesConceptosFacturaModel,
  LineaReajusteConceptoFacturaModel,
  ConceptoEnergiaFacturaGridRm,
  DXLoadOptions,
  LoadResult,
} from "@/.shared/.api";
import { useRouter } from "@/.shared/services/routing-services";
import { TipoOperacionEnum } from "@/.shared/enums/TipoOperacionEnum";
import DesgloseRetribucionEconomica from "@/.shared/components/operacion/DesgloseRetribucionEconomica.vue";
import FacturaReajusteModal from "@components/facturas/FacturaReajusteModal.vue";
import { messageBoxResult } from "@components/dialogs/message-box";
import { useModalService } from "@services/modal-service";
import { useIntl } from "@services/i18n";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import { importeRetribucionParaMostrar } from "@helpers/retribucion-economica-helper";

const props = withDefaults(defineProps<{
  templateData: DxDataGridTypes.MasterDetailTemplateData;
  showReajusteConceptosButtons: boolean;
}>(), {
  showReajusteConceptosButtons: false,
});

const emits = defineEmits(["updated-factura"]);

const api = useApi();
const grid = ref<InstanceType<typeof DxDataGrid>>();
const router = useRouter();
const modal = useModalService();
const intl = useIntl();

const claveFilaFacturaKeyExpr: (keyof ConceptoEnergiaFacturaGridRm)[] = ["IdTramite", "IdServicioIncluido"];
type ClaveFilaFactura = Pick<ConceptoEnergiaFacturaGridRm, "IdTramite" | "IdServicioIncluido">;

const filasFactura = shallowRef<ConceptoEnergiaFacturaGridRm[]>([]);
const filasFacturaOrigen = ref<ConceptoEnergiaFacturaGridRm[]>([]);

/** Invalida computeds que leen la selección vía la instancia del grid (no reactiva en Vue). */
const selectionEpoch = ref(0);

const tramitesGridOptions = computed((): DxDataGrid => ({
  ...getDefaultGridProperties(),
  remoteOperations: false,
  scrolling: { mode: "standard", rowRenderingMode: "standard"  },
  paging: { enabled: false }
}));

const idFactura = computed(() => props.templateData.key);
/** Reajustes permitidos en borrador o tras pasar a pendiente de revisión del colaborador (reajuste iterativo). */
const sePermiteReajusteConceptos = computed(() => {
  const estado = props.templateData.data?.Estado;
  return props.showReajusteConceptosButtons && (estado === "BORRADOR" || estado === "PDT_REVISION_COLABORADOR");
});

watch(sePermiteReajusteConceptos, (permite) => {
  if (!permite) {
    grid.value?.instance?.clearSelection();
    selectionEpoch.value++;
  }
});

function filasSeleccionadas(): ConceptoEnergiaFacturaGridRm[] {
  const inst = grid.value?.instance;
  if (!inst) {
    return [];
  }
  return (inst.getSelectedRowsData() as ConceptoEnergiaFacturaGridRm[]) ?? [];
}

function importesBaseHomogeneos(  filas: ConceptoEnergiaFacturaGridRm[],  obtenerBase: (fila: ConceptoEnergiaFacturaGridRm) => number | null,): boolean {
  const baseRef = obtenerBase(filas[0]);
  
  return filas.every((fila) => {
    const base = obtenerBase(fila);
    return base != null && base === baseRef;
  });
}

const puedeAsignarReajusteComision = computed(() => {
  selectionEpoch.value;
  if (!sePermiteReajusteConceptos.value) {
    return false;
  }
  const selected = filasSeleccionadas();
  if (selected.length <= 0) {
    return false;
  }
  return selected.every((row) => row.Comision != null) && importesBaseHomogeneos(selected, (row) => row.Comision);
});

const puedeAsignarReajusteRetrocomision = computed(() => {
  selectionEpoch.value;
  if (!sePermiteReajusteConceptos.value) {
    return false;
  }
  const selected = filasSeleccionadas();
  if (selected.length <= 0) {
    return false;
  }
  return selected.every((row) => row.Retrocomision != null) && importesBaseHomogeneos(selected, (row) => row.Retrocomision);
});

const hayCambiosReajusteVsSnapshot = computed(() => {
  for (const row of filasFactura.value) {
    const orig = filasFacturaOrigen.value.find((o) => mismaClave(o, row));
    if (!orig) {
      continue;
    }
    if (row.ReajusteComision !== orig.ReajusteComision || row.ReajusteRetrocomision !== orig.ReajusteRetrocomision) {
      return true;
    }
  }
  return false;
});

const puedeAplicarReajustes = computed(() => sePermiteReajusteConceptos.value && hayCambiosReajusteVsSnapshot.value);

function mismaClave(a: ClaveFilaFactura, b: ClaveFilaFactura): boolean {
  return a.IdTramite === b.IdTramite && a.IdServicioIncluido === b.IdServicioIncluido;
}

async function cargarTramites(): Promise<void> {
  const q: DXLoadOptions = {
    filter: ["IdFactura", "=", idFactura.value],
    requireTotalCount: false,
    skip: 0,
    take: 500,
  };

  const res = await api.callStandardApi("getTramitesEnergiaFacturaForGrid", { showLoading: false }, q) as LoadResult;
  const data = (res.data ?? []) as ConceptoEnergiaFacturaGridRm[];

  filasFacturaOrigen.value = structuredClone(data);
  filasFactura.value = data;
  grid.value?.instance?.clearSelection();
}

watch(
  () => props.templateData.key,
  () => {
    void cargarTramites();
  },
  { immediate: true }
);

function onSelectionChanged(): void {
  selectionEpoch.value++;
}

async function verDetalle(data: DevExpress.ui.dxDataGrid.ColumnButtonClickEvent) {
  const route = router.resolve({ name: "tramite", query: { idOperacion: data.row?.data.IdOperacion, tipoOperacion: TipoOperacionEnum.Energia, idTramite: data.row?.data.IdTramite, tipoTramite: data.row?.data.IdTipoSuministro } });
  window.open(route.href, "_blank");
}

function formatTotalComision(value: number | null): string {
  if (value == null || value === 0) {
    return "0 €";
  }

  return `${value.toFixed(2).replace(".", ",")} €`;
}

function formatTotalRetrocomision(value: number | null): string {
  if (value == null || value === 0) {
    return "0 €";
  }

  const mostrado = importeRetribucionParaMostrar(value, true) ?? 0;
  return `${mostrado.toFixed(2).replace(".", ",")} €`;
}

const onExporting = async () => {
  await exportGridToExcel(
    grid.value!.instance,
    "TramitesFactura.xlsx",
    "TramitesFactura",
    true,
  );
};

function redondearImporte(v: number): number {
  return Math.round(v * 100) / 100;
}

/** Mutaciones in-place sobre filas en `shallowRef`: notificar a Vue y repintar summary/celdas del grid. */
async function trasMutarFilasLocales(): Promise<void> {
  triggerRef(filasFactura);
  await nextTick();
  await grid.value?.instance?.refresh(true);
}

async function solicitarImporteReajuste(opts: {
  label: string;
  importeBase: number;
  importeTotal: number;
  importeReajuste: number | null;
  esRetrocomision?: boolean;
}): Promise<number | null | undefined> {
  const inputResponse = (await modal.showModal(
    FacturaReajusteModal,
    {
      label: opts.label,
      importeBase: opts.importeBase,
      importeTotal: opts.importeTotal,
      importeReajuste: opts.importeReajuste,
      esRetrocomision: opts.esRetrocomision ?? false,
      dxInputProps: { format: "#0.00 '€'", showSpinButtons: true, step: 0.01 },
      required: true,
    },
    {
      title: opts.label,
      width: 520,
      height: "auto",
      showTitle: true,
    },
  )) as { result: string; value: number | null };

  if (inputResponse?.result === messageBoxResult.ok) {
    return inputResponse.value;
  }
  return undefined;
}

async function onAsignarReajusteComision(): Promise<void> {
  const selected = filasSeleccionadas();
  if (selected.length === 0) {
    return;
  }

  const filaRef = selected[0];
  const label = intl.i18n.t("operaciones_factura.grid.cantidad_reajuste_comision");
  const cantidadRaw = await solicitarImporteReajuste({
    label,
    importeBase: filaRef.Comision!,
    importeTotal: filaRef.ComisionEfectiva ?? filaRef.Comision!,
    importeReajuste: filaRef.ReajusteComision,
  });

  if (cantidadRaw === undefined) {
    return;
  }

  const cantidad = cantidadRaw;
  if (cantidad == null || Number.isNaN(cantidad)) {
    toastError(intl.i18n.t("operaciones_factura.grid.cantidad_invalida"));
    return;
  }

  for (const target of selected) {
    target.ReajusteComision = redondearImporte(cantidad);
    target.ComisionEfectiva = redondearImporte(target.Comision! + cantidad);
  }
  await trasMutarFilasLocales();
}

async function onAsignarReajusteRetrocomision(): Promise<void> {
  const selected = filasSeleccionadas();
  if (selected.length === 0) {
    return;
  }

  const filaRef = selected[0];
  const label = intl.i18n.t("operaciones_factura.grid.cantidad_reajuste_retrocomision");
  const cantidadRaw = await solicitarImporteReajuste({
    label,
    importeBase: filaRef.Retrocomision!,
    importeTotal: filaRef.RetrocomisionEfectiva ?? filaRef.Retrocomision!,
    importeReajuste: filaRef.ReajusteRetrocomision,
    esRetrocomision: true,
  });

  if (cantidadRaw === undefined) {
    return;
  }

  const cantidad = cantidadRaw;
  if (cantidad == null || Number.isNaN(cantidad)) {
    toastError(intl.i18n.t("operaciones_factura.grid.cantidad_invalida"));
    return;
  }

  for (const target of selected) {
    target.ReajusteRetrocomision = redondearImporte(cantidad);
    target.RetrocomisionEfectiva = redondearImporte(target.Retrocomision! + cantidad);
  }

  await trasMutarFilasLocales();
}

function construirLineasReajuste(): { reajustesComision: LineaReajusteConceptoFacturaModel[]; reajustesRetrocomision: LineaReajusteConceptoFacturaModel[] } | null {
  const comision: LineaReajusteConceptoFacturaModel[] = [];
  const retrocomision: LineaReajusteConceptoFacturaModel[] = [];

  for (const row of filasFactura.value) {
    const orig = filasFacturaOrigen.value.find((o) => mismaClave(o, row));
    if (!orig) {
      continue;
    }

    if (row.ReajusteComision !== orig.ReajusteComision) {
      comision.push({
        IdTramite: row.IdTramite,
        IdServicioIncluido: row.IdServicioIncluido,
        Cantidad: row.ReajusteComision!,
      });
    }
    if (row.ReajusteRetrocomision !== orig.ReajusteRetrocomision) {
      retrocomision.push({
        IdTramite: row.IdTramite,
        IdServicioIncluido: row.IdServicioIncluido,
        Cantidad: row.ReajusteRetrocomision!,
      });
    }
  }

  if (comision.length === 0 && retrocomision.length === 0) {
    return null;
  }
  return { reajustesComision: comision, reajustesRetrocomision: retrocomision };
}

async function onAplicarReajustes(): Promise<void> {
  const lineas = construirLineasReajuste();
  if (lineas == null) {
    return;
  }

  const motivo = await modal.inputBox(intl.i18n.t("operaciones_factura.grid.motivo_reajustes"), "dxTextArea", null, true);

  if (motivo === undefined || motivo === null) {
    return;
  }

  const body = {
    motivo: motivo!,
    reajustesComision: lineas.reajustesComision,
    reajustesRetrocomision: lineas.reajustesRetrocomision,
  } as unknown as AplicarReajustesConceptosFacturaModel;

  try {
    await api.callStandardApi("aplicarReajustesConceptosFactura", { showLoading: true }, idFactura.value, body);
    toastSuccess(intl.i18n.t("operaciones_factura.grid.aplicar_reajustes_exito"));

    grid.value?.instance?.clearSelection();    
    await cargarTramites();

    emits("updated-factura");
  } catch {
    toastError(intl.i18n.t("operaciones_factura.grid.aplicar_reajustes_fallo"));
  }
}
</script>

<style lang="scss" scoped>

.exportar-excel {
  color: var(--text-success-default);
}
</style>
